import _ from 'lodash';
import { memo, useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { BFO_INSTRUMENTS } from 'v2/common/constants/index';
import { useFormContext, Controller } from 'react-hook-form';
import { LocaleUtils } from 'v2/common/utils/zeequantConstants';
import ErrorMesssage from '../ErrorMessage/index';
import CustomTypeAhead from 'v3/common/components/CustomTypeAhead';
import ApplicationDomainUtility from 'v3/common/utils/applicationDomain';
import { getInstrumentsLocaleWise } from 'v2/common/components/SingleInstrumentSelector/helper';

const propTypes = {
  instrument: PropTypes.string,
  onUpdateInstrument: PropTypes.func.isRequired,
  prependLabel: PropTypes.string,
};

const defaultProps = { instrument: '', prependLabel: '' };
const hideOptions = 'CDCX';


const SingleInstrumentSelector = ({ instrument: propsInstrument, onUpdateInstrument, prependLabel }) => {
  const { errors, control } = useFormContext();
  const { isAdmin: admin, isIndiaVersion } = ApplicationDomainUtility;
  const currentBroker = window.currentUserBroker || 'cryptobot';

  const uniqueId = useMemo(() => _.uniqueId('instrument_'), []);
  const instruments = useMemo(() => LocaleUtils().getAllSymbols(), []);
  const instrument = useMemo(() => propsInstrument.replace(/NFO:|BFO:|DEX:|CDCX:/, ''), [propsInstrument]);
  const typeaheadRef = useRef(null);
  const options = useMemo(
    () =>
      _.chain(getInstrumentsLocaleWise(currentBroker, instruments, isIndiaVersion))
        .map((ins) => ({
          label: ApplicationDomainUtility.isIndiaVersion ? ins : ins.split(':')[1],
          value: ApplicationDomainUtility.isIndiaVersion ? `${_.includes(BFO_INSTRUMENTS, ins) ? 'BFO' : 'NFO'}:${ins}` : ins,
        }))
        .filter((ins) =>
          ApplicationDomainUtility.getUiConfig('should_enable_bse') ||
          !BFO_INSTRUMENTS.includes(ins.label)
        ).value(),
    [instruments]
  );

  const onChangeInstrument = useCallback(
    (selectedInsArray, onChange) => {
      const selectedIns = _.get(selectedInsArray, '[0].value', '');
      onUpdateInstrument(selectedIns);
      onChange(selectedInsArray);
    },
    [onUpdateInstrument]
  );

  const selectedOptions = instrument ? [instrument] : [];
  const errorMessage = _.get(errors, `${uniqueId}.message`, '');

  const validatorConfig = useMemo(
    () => ({
      validate: (value) => {
        if (_.isEmpty(value)) return 'Please add instrument';

        return null;
      },
    }),
    []
  );

  return (
    <div>
      <div className="d-flex align-items-center gap-5 flex-wrap">
        <div className="d-flex align-items-center single-instrument-selector  flex-column">
          <div className="input-group">
            {prependLabel && (
              <div className="input-group-prepend main-prepend">
                <div className="input-group-text">{prependLabel}</div>
              </div>
            )}
            <Controller
              render={({ onChange, name }) => (
                <CustomTypeAhead
                  className="custom-form-control"
                  isInvalid={!!errorMessage}
                  ref={typeaheadRef}
                  clearButtons
                  name={name}
                  customKey={name}
                  options={options}
                  clearButton
                  defaultSelected={selectedOptions}
                  placeholder="Search..."
                  onChange={(newInstruments) => {
                    if (newInstruments.length === 0 && typeaheadRef.current) {
                      typeaheadRef.current?.focus();
                      typeaheadRef.current?.toggleMenu();
                    }
                    onChangeInstrument(newInstruments, onChange);
                  }}
                />
              )}
              control={control}
              defaultValue={selectedOptions}
              value={selectedOptions}
              name={uniqueId}
              rules={validatorConfig}
            />
          </div>
        </div>
      </div>
      <ErrorMesssage message={errorMessage} />
    </div>
  );
};

SingleInstrumentSelector.propTypes = propTypes;
SingleInstrumentSelector.defaultProps = defaultProps;

export default memo(SingleInstrumentSelector);
